import type { ReactNode } from "react";
import { format } from "date-fns";
import {
  ACTIVITY_EVENT_ACTOR_TYPE,
  type OpportunityActivityEvent,
} from "@forgepoint/shared";
import { useOrgMembers } from "@/store/teamsStore";
import {
  OpportunityActivityLine,
  type ActivityLineEmphasis,
} from "./OpportunityActivityLine";
import {
  activityActor,
  activityMessage,
  buildActivitySentence,
  withViewCount,
  type ActivityNames,
} from "./opportunityActivitySentences";

interface OpportunityActivityEntryProps {
  event: OpportunityActivityEvent;
  viewCount: number;
  names: ActivityNames;
  showActorName?: boolean;
  onOpenThread: (threadId: string) => void;
  onOpenQuote: (quoteId: string) => void;
  trailing?: ReactNode;
}

/**
 * One opportunity event as an activity line.
 * @param event - The event being rendered
 * @param viewCount - How many views of the quote the line stands for
 * @param names - The names ids resolve through
 * @param showActorName - Whether the sentence names the actor
 * @param onOpenThread - Opens an agent thread
 * @param onOpenQuote - Opens a quote
 * @param trailing - Rendered after the sentence
 */
export const OpportunityActivityEntry = ({
  event,
  viewCount,
  names,
  showActorName = true,
  onOpenThread,
  onOpenQuote,
  trailing,
}: OpportunityActivityEntryProps) => {
  const members = useOrgMembers();
  const threadId =
    event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.AGENT_SDK_THREAD
      ? event.actorId
      : null;

  return (
    <OpportunityActivityLine
      actor={activityActor(event, names.agentName, members)}
      sentence={withViewCount(buildActivitySentence(event, names), viewCount)}
      time={format(event.createdAt, "h:mm a")}
      emphasis={emphasisOf(event)}
      message={activityMessage(event)}
      showActorName={showActorName}
      onOpenActor={threadId ? () => onOpenThread(threadId) : null}
      onOpenQuote={onOpenQuote}
      trailing={trailing}
    />
  );
};

// A customer's action stands out; a teammate's quote view recedes.
function emphasisOf(event: OpportunityActivityEvent): ActivityLineEmphasis {
  if (
    event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.EXTERNAL ||
    event.eventType === "QUOTE_SIGNED"
  ) {
    return "external";
  }
  if (event.eventType === "QUOTE_VIEWED") {
    return "muted";
  }
  return "default";
}
